/**
 * The money layer of a paid report (owner decision B-05, 2026-09-04).
 *
 * The audit is a screenshot plus DOM and axe checks. It has no behavioral
 * data, so it can never measure revenue impact. Everything here is arithmetic
 * over numbers the client supplied at checkout, and the framing is a
 * break-even, never a forecast: it states what one additional conversion is
 * worth and how many findings sit on the path to it. Nothing predicts a lift.
 *
 * Deterministic and unit-tested. No model touches these numbers.
 */

import type { AuditFinding, SeverityLevel } from '@/types/audit';

export const SUCCESS_ACTIONS = ['purchase', 'contact', 'signup', 'inform'] as const;
export type SuccessAction = (typeof SUCCESS_ACTIONS)[number];

export function isSuccessAction(v: unknown): v is SuccessAction {
  return typeof v === 'string' && (SUCCESS_ACTIONS as readonly string[]).includes(v);
}

export interface ConversionInputs {
  successAction: SuccessAction | null;
  /** What one completed action is worth, in cents. Required for a money report. */
  conversionValueCents: number | null;
  /** Optional refinement for clients who have analytics. */
  monthlySessions?: number | null;
  /** Share of sessions that complete the action today, 0..1. Optional. */
  actionRate?: number | null;
}

export interface RecoveryScenario {
  /** Share of non-completing visitors recovered; an assumption the reader sets. */
  recovery: number;
  /** Monthly value at that recovery, in cents. */
  monthlyCents: number;
}

export type BreakEven =
  | {
      kind: 'money';
      successAction: Exclude<SuccessAction, 'inform'>;
      conversionValueCents: number;
      onPathCount: number;
      onPathQuickCount: number;
      totalFindings: number;
      /** Present only when monthlySessions and actionRate were both given. */
      scenarios?: { notCompletingPerMonth: number; list: RecoveryScenario[] };
    }
  | { kind: 'task'; onPathCount: number; totalFindings: number }
  | { kind: 'missing'; reason: 'no-action' | 'no-value' };

export const RECOVERY_SCENARIOS = [0.02, 0.05, 0.1] as const;

const SEVERITY_RANK: Record<SeverityLevel, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const EFFORT_RANK: Record<string, number> = { quick: 0, medium: 1, complex: 2 };

/**
 * Commercial order: on the success path first, then severity, then quick fixes
 * ahead of complex ones, then the original order. A critical footer defect no
 * longer outranks a medium friction point on the contact form.
 */
export function rankFindings<T extends AuditFinding>(findings: T[]): T[] {
  return findings
    .map((f, i) => ({ f, i }))
    .sort((a, b) => {
      const path = Number(Boolean(b.f.onActionPath)) - Number(Boolean(a.f.onActionPath));
      if (path !== 0) return path;
      const sev = SEVERITY_RANK[a.f.severity] - SEVERITY_RANK[b.f.severity];
      if (sev !== 0) return sev;
      const eff = (EFFORT_RANK[a.f.effortEstimate ?? ''] ?? 1) - (EFFORT_RANK[b.f.effortEstimate ?? ''] ?? 1);
      if (eff !== 0) return eff;
      return a.i - b.i;
    })
    .map((x) => x.f);
}

export function computeBreakEven(inputs: ConversionInputs, findings: AuditFinding[]): BreakEven {
  const onPath = findings.filter((f) => f.onActionPath === true);
  if (!inputs.successAction) return { kind: 'missing', reason: 'no-action' };
  if (inputs.successAction === 'inform') {
    return { kind: 'task', onPathCount: onPath.length, totalFindings: findings.length };
  }
  const value = inputs.conversionValueCents;
  if (!value || !Number.isFinite(value) || value <= 0) return { kind: 'missing', reason: 'no-value' };

  const result: Extract<BreakEven, { kind: 'money' }> = {
    kind: 'money',
    successAction: inputs.successAction,
    conversionValueCents: Math.round(value),
    onPathCount: onPath.length,
    onPathQuickCount: onPath.filter((f) => f.effortEstimate === 'quick').length,
    totalFindings: findings.length,
  };

  const sessions = inputs.monthlySessions ?? null;
  const rate = inputs.actionRate ?? null;
  if (sessions && sessions > 0 && rate !== null && rate >= 0 && rate <= 1) {
    const notCompleting = Math.round(sessions * (1 - rate));
    result.scenarios = {
      notCompletingPerMonth: notCompleting,
      list: RECOVERY_SCENARIOS.map((recovery) => ({
        recovery,
        monthlyCents: Math.round(notCompleting * recovery * value),
      })),
    };
  }
  return result;
}

// ---------------------------------------------------------------------------
// Input parsing. The output is a figure a growth lead will quote upward, so
// the input gets more care than most: currency symbols and thousands
// separators are tolerated, the range is bounded, and values that are
// plausible but unusual are flagged for a confirmation step rather than
// silently accepted.
// ---------------------------------------------------------------------------

export const MIN_CONVERSION_VALUE_CENTS = 100; // $1
export const MAX_CONVERSION_VALUE_CENTS = 1_000_000_00; // $1,000,000
export const IMPLAUSIBLE_BELOW_CENTS = 10_00; // under $10, ask
export const IMPLAUSIBLE_ABOVE_CENTS = 250_000_00; // over $250,000, ask

export type ParsedValue =
  | { ok: true; cents: number; confirm: boolean }
  | { ok: false; reason: 'empty' | 'not-a-number' | 'too-small' | 'too-large' };

export function parseConversionValue(raw: unknown): ParsedValue {
  if (raw === null || raw === undefined) return { ok: false, reason: 'empty' };
  const s = String(raw).trim().replace(/[$,\s]/g, '').replace(/^USD/i, '');
  if (!s) return { ok: false, reason: 'empty' };
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return { ok: false, reason: 'not-a-number' };
  const cents = Math.round(Number(s) * 100);
  if (cents < MIN_CONVERSION_VALUE_CENTS) return { ok: false, reason: 'too-small' };
  if (cents > MAX_CONVERSION_VALUE_CENTS) return { ok: false, reason: 'too-large' };
  return { ok: true, cents, confirm: cents < IMPLAUSIBLE_BELOW_CENTS || cents > IMPLAUSIBLE_ABOVE_CENTS };
}

/**
 * The optional refinement for clients who have analytics: monthly sessions to
 * the page and the share of them that complete the action today. Both are
 * optional and the model only uses them together; a lone value is stored as
 * null so the report never guesses the other half. The rate arrives as a
 * percentage typed by a person ("2.1", "2,1", "2.1%") and is stored as 0..1.
 */
export interface OptionalRefinement {
  monthlySessions: number | null;
  actionRate: number | null;
}

export function parseOptionalRefinement(rawSessions: unknown, rawRate: unknown): OptionalRefinement {
  const s = String(rawSessions ?? '').trim().replace(/[,\s]/g, '');
  const r = String(rawRate ?? '').trim().replace(/[%\s]/g, '').replace(',', '.');
  const sessions = /^\d{1,9}$/.test(s) ? Number(s) : null;
  const rate = /^\d{1,3}(\.\d{1,3})?$/.test(r) && Number(r) <= 100 ? Number(r) / 100 : null;
  if (sessions === null || rate === null) return { monthlySessions: null, actionRate: null };
  return { monthlySessions: sessions, actionRate: Math.round(rate * 100000) / 100000 };
}

export function formatCents(cents: number, locale: 'en' | 'es' = 'en'): string {
  return new Intl.NumberFormat(locale === 'es' ? 'es-ES' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(cents) / 100);
}
